import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';

function assignRef(ref, value) {
  if (typeof ref === 'function') {
    ref(value);
  } else if (ref) {
    ref.current = value;
  }
}

const ScrollFade = forwardRef(function ScrollFade(
  {
    children,
    className = '',
    containerClassName = '',
    fadeClassName = '',
    fadeBlurClassName = 'backdrop-blur-[2px]',
    showTopFade = false,
    topFadeClassName = '',
    showScrollIndicator = false,
    scrollIndicatorOffset = '0px',
    scrollIndicatorClassName = '',
    onScroll,
    ...props
  },
  forwardedRef,
) {
  // CON-165: the fade's default height gives way to a height the caller passes
  // in fadeClassName. On Tailwind v3 the caller's class won by stylesheet order;
  // v4 orders height utilities differently, so the two must not be combined.
  // Only an UNPREFIXED height conflicts: a variant height (`sm:h-6`) always
  // follows the base one in both versions, and v3 rendered both — so it
  // deliberately does not suppress the default.
  const fadeHeightClass = /(^|\s)h-/.test(fadeClassName) ? '' : 'h-12';
  const scrollRef = useRef(null);
  const scrollTimeoutRef = useRef(null);
  const [showBottomFade, setShowBottomFade] = useState(false);
  const [showTopFadeState, setShowTopFadeState] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);
  const [scrollThumbStyle, setScrollThumbStyle] = useState(null);

  const setRefs = useCallback(
    (node) => {
      scrollRef.current = node;
      assignRef(forwardedRef, node);
    },
    [forwardedRef],
  );

  const updateBottomFade = useCallback(() => {
    const scrollBody = scrollRef.current;
    if (!scrollBody) return;

    const canScroll = scrollBody.scrollHeight > scrollBody.clientHeight + 1;
    const isAtTop = scrollBody.scrollTop <= 2;
    const isAtBottom =
      scrollBody.scrollTop + scrollBody.clientHeight >= scrollBody.scrollHeight - 2;

    if (showScrollIndicator && canScroll) {
      const thumbHeight = Math.max(
        28,
        (scrollBody.clientHeight / scrollBody.scrollHeight) * scrollBody.clientHeight,
      );
      const maxThumbTop = scrollBody.clientHeight - thumbHeight;
      const scrollableDistance = scrollBody.scrollHeight - scrollBody.clientHeight;
      const thumbTop =
        scrollableDistance > 0 ? (scrollBody.scrollTop / scrollableDistance) * maxThumbTop : 0;

      setScrollThumbStyle({
        height: `${thumbHeight}px`,
        transform: `translateY(${thumbTop}px)`,
      });
    } else {
      setScrollThumbStyle(null);
    }

    setShowTopFadeState(showTopFade && canScroll && !isAtTop);
    setShowBottomFade(canScroll && !isAtBottom);
  }, [showScrollIndicator, showTopFade]);

  const handleScroll = useCallback(
    (event) => {
      updateBottomFade();
      if (showScrollIndicator) {
        setIsScrolling(true);
        if (scrollTimeoutRef.current) {
          window.clearTimeout(scrollTimeoutRef.current);
        }
        scrollTimeoutRef.current = window.setTimeout(() => {
          setIsScrolling(false);
        }, 700);
      }
      onScroll?.(event);
    },
    [onScroll, showScrollIndicator, updateBottomFade],
  );

  const showTransientScrollIndicator = useCallback(() => {
    if (!showScrollIndicator) return;

    updateBottomFade();
    setIsScrolling(true);
    if (scrollTimeoutRef.current) {
      window.clearTimeout(scrollTimeoutRef.current);
    }
    scrollTimeoutRef.current = window.setTimeout(() => {
      setIsScrolling(false);
    }, 700);
  }, [showScrollIndicator, updateBottomFade]);

  useEffect(() => {
    updateBottomFade();
    const animationFrameId = window.requestAnimationFrame(updateBottomFade);
    const timeoutId = window.setTimeout(updateBottomFade, 0);

    const scrollBody = scrollRef.current;
    if (!scrollBody) {
      return () => {
        window.cancelAnimationFrame(animationFrameId);
        window.clearTimeout(timeoutId);
      };
    }

    let resizeObserver;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(updateBottomFade);
      resizeObserver.observe(scrollBody);
      Array.from(scrollBody.children).forEach((child) => resizeObserver.observe(child));
    }

    let mutationObserver;
    if (typeof MutationObserver !== 'undefined') {
      mutationObserver = new MutationObserver(updateBottomFade);
      mutationObserver.observe(scrollBody, {
        attributes: true,
        childList: true,
        subtree: true,
      });
    }

    return () => {
      window.cancelAnimationFrame(animationFrameId);
      window.clearTimeout(timeoutId);
      if (scrollTimeoutRef.current) {
        window.clearTimeout(scrollTimeoutRef.current);
      }
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
    };
  }, [children, updateBottomFade]);

  return (
    <div
      className={`relative ${showScrollIndicator ? 'overflow-visible' : 'overflow-hidden'} ${containerClassName}`}
    >
      <div
        ref={setRefs}
        className={className}
        onScroll={handleScroll}
        onWheel={showTransientScrollIndicator}
        onTouchMove={showTransientScrollIndicator}
        {...props}
      >
        {children}
      </div>

      {showTopFadeState && (
        <div
          className={`pointer-events-none absolute inset-x-0 top-0 h-12 bg-gradient-to-b from-paper-light via-paper-light/65 to-paper-light/0 ${fadeBlurClassName} [mask-image:linear-gradient(to_bottom,black_0%,black_35%,transparent_100%)] ${topFadeClassName}`}
        />
      )}

      {showScrollIndicator && scrollThumbStyle && (
        <div
          className={`pointer-events-none absolute inset-y-2 right-0 z-10 w-[6px] rounded-full transition-opacity duration-150 ${
            isScrolling ? 'opacity-100' : 'opacity-0'
          } ${scrollIndicatorClassName}`}
          style={{
            right: scrollIndicatorOffset,
            backgroundColor: 'rgba(58, 52, 41, 0.12)',
          }}
        >
          <div
            className="absolute left-0 top-0 w-full rounded-full shadow-sm"
            style={{
              ...scrollThumbStyle,
              backgroundColor: 'rgba(107, 100, 88, 0.72)',
            }}
          />
        </div>
      )}

      {showBottomFade && (
        <div
          className={`pointer-events-none absolute inset-x-0 bottom-0 ${fadeHeightClass} bg-gradient-to-t from-paper-light via-paper-light/65 to-paper-light/0 ${fadeBlurClassName} [mask-image:linear-gradient(to_top,black_0%,black_35%,transparent_100%)] ${fadeClassName}`}
        />
      )}
    </div>
  );
});

export default ScrollFade;
